import React, { useRef } from "react";
import { Button } from "primereact/button";
import { DataView, DataViewLayoutOptions } from "primereact/dataview";
import { Tag } from "primereact/tag";
import { SelectButton } from "primereact/selectbutton";
import { Image } from "primereact/image";
import { BannerDialog } from "../components/dialogs/BannerDialog";
import { EliminarBannerDialog } from "../components/dialogs/EliminarBanner";
import AppToast from "../../../shared/components/AppToast";
import useBanners from "../hooks/useBanners";
import { getCompany } from "../../../config/companyConfig";

const canal_options = [
    { label: "B2B", value: "B2B" },
    { label: "B2C", value: "B2C" },
];

const Banners = () => {
    const toastRef = useRef(null);
    const [canal, setCanal] = React.useState(getCompany().defaultCanal);
    const {
        banners,
        loading,
        togglingId,
        handleToggleEstado,
        editarDialog,
        eliminarDialog,
        handleEliminar,
        handleSave,
    } = useBanners({
        canal,
        onError: (err) => toastRef.current?.show({ severity: "error", summary: "Error", detail: err?.message || "Ocurrió un error.", life: 4000 }),
    });

    const actions = (banner) => {
        const activa = banner?.activa;

        return (
            <div className="flex flex-row md:flex-column gap-2">
                <div className="gap-3 flex">
                     <Button
                        icon="pi pi-pencil"
                        tooltip="Editar"
                        tooltipOptions={{ position: "top" }}
                        onClick={() => editarDialog.open(banner)}
                        size="small"
                        outlined
                    />

                    <Button
                        size="small"
                        icon="pi pi-power-off"
                        aria-label={activa ? "Desactivar" : "Activar"}
                        tooltip={activa ? "Desactivar" : "Activar"}
                        tooltipOptions={{ position: "top" }}
                        onClick={() => handleToggleEstado(banner)}
                        severity={activa ? "warning" : "success"}
                        loading={togglingId === (banner.id || banner.codigo)}
                        disabled={togglingId !== null}
                        outlined
                    />
            
                    <Button
                        size="small" 
                        severity="danger" 
                        icon="pi pi-trash"
                        tooltipOptions={{ position: "top" }}
                        tooltip="Eliminar"
                        onClick={() => eliminarDialog.open(banner)}
                        disabled={togglingId !== null}
                        outlined
                    />
                </div>
            </div>
        );
    };

    const bannerImage = (banner) => {
        if (banner?.foto && banner?.mime) {
            return <Image src={`data:${banner.mime};base64,${banner.foto}`} alt={banner.titulo || ""} preview className="w-full border-round" imageClassName="w-full" imageStyle={{ height: "90px", objectFit: "contain" }} />;
        }
        return <div className="w-full border-round" style={{ background: "#e5e7eb", height: "90px" }} />;
    };

    const estadoBadge = (banner) => (
        <Tag value={banner?.activa ? "Activo" : "Inactivo"} severity={banner?.activa ? "success" : "warning"} />
    );

    const listItem = (banner) => (
        <div className="col-12" key={banner.id || banner.codigo}>
            <div className="flex flex-column md:flex-row align-items-start gap-3 p-3 border-bottom-1 surface-border">
                <div className="flex-1">
                    <div className="text-lg font-semibold mb-2 flex align-items-center gap-2">
                        {banner.titulo || banner.descripcion || "Sin título"}
                        {estadoBadge(banner)}
                    </div>
                    {actions(banner)}
                </div>
                <div className="w-full md:w-12rem">{bannerImage(banner)}</div>
            </div>
        </div>
    );

    const gridItem = (banner) => (
        <div className="col-12 sm:col-6 lg:col-3 p-2" key={banner.id || banner.codigo}>
            <div className="border-1 surface-border border-round p-3">
                <div className="flex justify-content-between align-items-center mb-2 gap-2">
                    <span className="text-sm font-semibold">{banner.titulo || banner.descripcion || "Sin título"}</span>
                    {estadoBadge(banner)}
                </div>
                {bannerImage(banner)}
                <div className="mt-2">{actions(banner)}</div>
            </div>
        </div>
    );

    const itemTemplate = (banner, view) => {
        if (!banner) return null;
        if (view === "list") return listItem(banner);
        return gridItem(banner);
    };

    const [layout, setLayout] = React.useState("grid");

    const header = (
        <div className="flex justify-content-between align-items-center">
            <span className="text-xl font-semibold">Banners</span>
            <SelectButton
                value={canal}
                options={canal_options}
                onChange={(e) => { if (e.value && e.value !== canal) setCanal(e.value); }}
            />
            <DataViewLayoutOptions layout={layout} onChange={(e) => setLayout(e.value)} />
            <Button icon="pi pi-plus" label="Agregar" onClick={() => editarDialog.open(null)} severity="success" disabled={togglingId !== null} />
        </div>
    );

    return (
        <>
            <AppToast ref={toastRef} />
            <div className="py-3">
                <div className="grid">
                    <div className="col-12">
                        <h4 className="font-bold mb-0">Administración de Banners</h4>
                        <p className="text-700 text-lg line-height-3 mb-3">
                            Aqui puedes administrar tu banners.
                        </p> 
                    </div>
                        <div className="border-1 col-12 surface-border border-round">
                            <DataView 
                                header={header} 
                                value={banners} 
                                itemTemplate={itemTemplate} 
                                layout={layout} 
                                paginator 
                                rows={8} 
                                rowsPerPageOptions={[16, 24, 32]} 
                                loading={loading} 
                            />
                        </div>
                </div>
            </div>

            <BannerDialog
                visible={editarDialog.visible}
                onCancel={editarDialog.close}
                onSave={handleSave}
                banner={editarDialog.selectedItem}
                defaultTipo={canal}
            />

            <EliminarBannerDialog
                visible={eliminarDialog.visible}
                onCancel={eliminarDialog.close}
                onConfirm={handleEliminar}
            />
        </>
    );
};

export default Banners;
